import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  IconButton,
  Image,
  Input,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from "@chakra-ui/react";
import { Form, Formik, useFormikContext } from "formik";
import { useVirtualizer } from "@tanstack/react-virtual";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import StatusBadge from "@/shared/components/StatusBadge";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickMutationProduct,
  PathPickProduct,
  PathPickProductKey,
} from "@/features/pathpick/types";
import { PathPickOrderItemRequestBody } from "@/features/pathpick/types/request-body";
import { PathPickCreatedOrderResponse } from "@/features/pathpick/types/response";
import {
  getProductImageUrl,
  isProductImageAiCollected,
  toPathPickProductKey,
} from "@/features/pathpick/utils";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";
import useCreatePathPickOrder from "@/features/pathpick/hooks/mutation/useCreatePathPickOrder";
import PathPickOrderCompleteModal from "@/features/pathpick/components/order/PathPickOrderCompleteModal";
import { useQueryClient } from "@tanstack/react-query";
import useGetBuyerBankInfo from "@/shared/hooks/bank-info/useGetBuyerBankInfo";
import useGetBuyerRemitterName, {
  buyerRemitterNameQueryKey,
} from "@/shared/hooks/bank-info/useGetBuyerRemitterName";
import ShippingInfoSelectModal from "@/shared/components/shipping-info/ShippingInfoSelectModal";
import { ShippingInfoResponse } from "@/shared/api/shipping-infos/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  items: PathPickProduct[];
  // 주문이 접수된 직후 호출된다 — 실제로 주문에 담긴 상품만 넘긴다(수량 0인 상품은 빠진다).
  // 유형까지 넘겨야 다른 유형의 같은 숫자 id를 구분한다.
  // 담은 상품 페이지가 선택 해제에 쓴다. 상품 찾기의 단건 주문은 넘기지 않는다.
  onOrderCompleted?: (orderedProducts: PathPickMutationProduct[]) => void;
  // 시작 가이드의 주문 체험 — 수량 입력 화면까지만 실제와 똑같이 보여주고,
  // 배송지 선택(주소 조회)·주문 생성·완료 모달(주문 상세로 이동)은 전부 건너뛴다.
  // 목 체험이 실제 주문 내역을 만들면 안 되기 때문이다.
  isTutorial?: boolean;
  // 시작 가이드 전용 — 상품별로 이만큼 정확히 입력해야 주문 요청이 열린다.
  // 넘기지 않으면(실제 주문) 지금까지처럼 수량 합이 1 이상이기만 하면 된다.
  requiredQuantityByProductId?: Record<number, number>;
}

// 상품 키("{type}:{id}") -> (optionName -> 수량)
// 상품 키에 유형을 붙이는 이유: 단품오더와 라인시트 상품은 숫자 id가 겹쳐 일괄 주문에서 수량이 섞인다.
// 옵션 키는 화면 표시명(displayName)이 아니라 API 정본인 option.name이다 — 주문 생성 시 그대로 전송한다.
type Quantities = Record<PathPickProductKey, Record<string, number>>;

const toItemKey = (item: PathPickProduct) =>
  toPathPickProductKey(item.productType, item.id);

interface OrderQuantityFormValues {
  quantities: Quantities;
}

const CELL_BORDER = "0.5px solid #D4D4D1";
const PRODUCT_COLUMN_WIDTH = "300px";
// 상품 열은 왼쪽에 고정되므로 좁은 폭에서 300px면 나머지 열이 보이는 폭이 거의 남지 않는다.
// 220px에서도 텍스트 칸(220 − 패딩 24 − 이미지 56 − gap 8 = 132px)에 배지 줄이 한 줄로 들어가
// 아래 고정 행 높이 계산이 그대로 유지된다(나머지 텍스트는 noOfLines로 말줄임).
const PRODUCT_COLUMN_WIDTH_MOBILE = "220px";
const OPTION_COLUMN_WIDTH = "110px";

// 행 높이 고정 — 가상화가 스크롤 오프셋을 계산하는 기준값이다.
// 가장 높은 셀은 상품 셀:
//   padding 12 + max(이미지 48, 텍스트열 브랜드 20 + 상품명 20 + SKU 16 + 배지행 28 + gap 2×3 = 90) + padding 12 = 114
//   + 셀 하단 테두리 0.5 = 114.5  → 올림해서 115
// 이 값보다 내용이 크면 grid의 auto 트랙이 컨테이너 밖으로 넘치고,
// 다음 행의 흰 배경이 이전 행의 하단 구분선을 덮어 가로 구분선이 사라진다.
// 상품 셀 안의 텍스트에 전부 noOfLines를 건 것도 이 높이를 확정하기 위해서다.
const ROW_HEIGHT = 115;

// 필수 수량 안내(가이드 전용)가 붙으면 옵션 셀에 캡션 한두 줄이 더 쌓인다 —
// 위 계산에 그 높이를 더한 값. 이 값을 안 올리면 행이 넘쳐 가로 구분선이 사라진다.
const ROW_HEIGHT_WITH_QUANTITY_HINT = 140;

const headerFillerCellStyle = {
  padding: "12px",
  background: "#F7F9FA",
  borderBottom: CELL_BORDER,
};

const bodyFillerCellStyle = {
  padding: "12px",
  background: "#FFFFFF",
  borderBottom: CELL_BORDER,
};

// filler 셀은 우측 경계선이 없어야 테이블이 끊긴 것처럼 보이지 않는다.
const headerCellStyle = {
  ...headerFillerCellStyle,
  borderRight: CELL_BORDER,
};

const bodyCellStyle = {
  ...bodyFillerCellStyle,
  borderRight: CELL_BORDER,
};

// 상품별·보유 옵션별 0으로 초기화한 formik 초기값
const buildInitialQuantities = (items: PathPickProduct[]): Quantities =>
  Object.fromEntries(
    items.map((item) => [
      toItemKey(item),
      Object.fromEntries(item.options.map((option) => [option.name, 0])),
    ])
  );

const toOrderedProduct = ({
  productId,
  productType,
}: PathPickOrderItemRequestBody): PathPickMutationProduct => ({
  id: productId,
  type: productType,
});

interface OrderQuantityFormProps {
  items: PathPickProduct[];
  onClose: () => void;
  isSubmitting: boolean;
  requiredQuantityByProductId?: Record<number, number>;
}

// Formik 자식으로 분리한 이유: useVirtualizer 등 훅을 써야 하는데
// render prop 안에서는 훅을 호출할 수 없다. 값은 useFormikContext로 읽는다.
function OrderQuantityForm({
  items,
  onClose,
  isSubmitting,
  requiredQuantityByProductId,
}: OrderQuantityFormProps) {
  const { t, tNumberString, tCurrencyString } = useI18nHelper();
  const { values, setFieldValue } = useFormikContext<OrderQuantityFormValues>();

  const formatKrw = (value: number) =>
    tCurrencyString(value, { type: "SYMBOL", code: "KRW" });

  const [showOnlyWithQuantity, setShowOnlyWithQuantity] =
    useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 옵션은 행마다 1번 칼럼부터 좌측 정렬되므로, 칼럼 수는 최대 옵션 개수면 된다.
  const maxOptionCount = useMemo(
    () => items.reduce((max, item) => Math.max(max, item.options.length), 0),
    [items]
  );

  // repeat(0, ...)은 invalid CSS이므로 옵션이 없으면 옵션 칼럼을 생략한다.
  const optionColumns =
    maxOptionCount > 0
      ? ` repeat(${maxOptionCount}, ${OPTION_COLUMN_WIDTH})`
      : "";
  // 마지막 1fr은 남는 폭을 채우는 filler — 헤더 배경이 우측 끝까지 이어지게 한다.
  const buildTemplateColumns = (productColumnWidth: string) =>
    `${productColumnWidth} 140px 140px 140px 140px${optionColumns} 1fr`;
  const templateColumns = {
    base: buildTemplateColumns(PRODUCT_COLUMN_WIDTH_MOBILE),
    md: buildTemplateColumns(PRODUCT_COLUMN_WIDTH),
  };

  const getItemQuantity = (item: PathPickProduct, optionName: string) =>
    values.quantities[toItemKey(item)]?.[optionName] ?? 0;

  const getItemTotalQuantity = (item: PathPickProduct) =>
    item.options.reduce(
      (sum, option) => sum + getItemQuantity(item, option.name),
      0
    );

  const getItemAmount = (item: PathPickProduct) =>
    getItemTotalQuantity(item) * item.salePrice.value;

  const totalQuantity = items.reduce(
    (sum, item) => sum + getItemTotalQuantity(item),
    0
  );
  const totalAmount = items.reduce((sum, item) => sum + getItemAmount(item), 0);

  const getRequiredQuantity = (item: PathPickProduct) =>
    requiredQuantityByProductId?.[item.id];

  // 필수 수량이 지정된 상품은 그 수량과 정확히 같아야 한다.
  // 지정이 없는 상품(=실제 주문의 모든 상품)은 아무 제약도 받지 않는다.
  const isRequiredQuantityMet = items.every((item) => {
    const required = getRequiredQuantity(item);
    return required === undefined || getItemTotalQuantity(item) === required;
  });

  const rowHeight = requiredQuantityByProductId
    ? ROW_HEIGHT_WITH_QUANTITY_HINT
    : ROW_HEIGHT;

  const handleChangeQuantity = (
    item: PathPickProduct,
    optionName: string,
    rawValue: string,
    maxStock: number
  ) => {
    const parsed = Number(rawValue);
    const next = isNaN(parsed)
      ? 0
      : Math.min(Math.max(Math.floor(parsed), 0), maxStock);
    // 옵션명(예: "GREY|M4.5/W6")을 경로에 넣으면 formik이 lodash toPath로 쪼개
    // ["quantities","123","GREY|M4","5/W6"]가 되어 엉뚱한 곳에 값이 쓰인다.
    // ('.'뿐 아니라 '['·']'도 경로 문법이다.)
    // 옵션명은 반드시 "값" 안의 키로만 쓴다 — handleBuyAll과 동일한 방식.
    // 상품 키("PRODUCT:123")에는 경로 문법 문자가 없어 그대로 경로에 써도 된다.
    const itemKey = toItemKey(item);
    setFieldValue(`quantities.${itemKey}`, {
      ...values.quantities[itemKey],
      [optionName]: next,
    });
  };

  // 전량구매: 해당 상품 모든 옵션을 재고 수량으로 채운다.
  const handleBuyAll = (item: PathPickProduct) => {
    setFieldValue(
      `quantities.${toItemKey(item)}`,
      Object.fromEntries(
        item.options.map((option) => [option.name, option.stock])
      )
    );
  };

  // 이미 모든 옵션이 재고만큼 입력돼 있으면 전량구매 비활성화
  const isBuyAllDisabled = (item: PathPickProduct) =>
    item.options.every(
      (option) => getItemQuantity(item, option.name) === option.stock
    );

  const visibleItems = showOnlyWithQuantity
    ? items.filter((item) => getItemTotalQuantity(item) > 0)
    : items;

  const rowVirtualizer = useVirtualizer({
    count: visibleItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  // 화면 밖 행들은 위/아래 스페이서 높이로만 표현한다.
  // 절대 위치 + translateY 대신 일반 흐름을 유지해야 상품 칼럼의 sticky 가로 고정이 깨지지 않는다.
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    <Form style={{ display: "contents" }}>
      {/* 헤더는 바디와 동일한 흰 배경을 유지한다. */}
      <ModalHeader padding={"16px"} borderBottom={CELL_BORDER}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Flex flexDirection={"column"} gap={"2px"}>
            <Text textStyle={"body-md-bold"} color={"#444444"}>
              {t("path_pick:basket.order_modal.title")}
            </Text>
            {/*<Text textStyle={"caption-medium"} color={"#8F8F8C"}>*/}
            {/*  {t("path_pick:basket.order_modal.description")}*/}
            {/*</Text>*/}
          </Flex>
          <IconButton
            aria-label="Close"
            icon={<CustomIcon src={"x"} width={28} height={28} />}
            variant={"ghost"}
            onClick={onClose}
            color={"#8F8F8C"}
            size={"sm"}
            _hover={{ background: "transparent" }}
          />
        </Flex>
      </ModalHeader>
      {/* 스크롤은 아래 scrollRef 박스 하나가 전담한다. ModalBody는 세로 배치만 한다. */}
      <ModalBody
        padding={0}
        display={"flex"}
        flexDirection={"column"}
        overflow={"hidden"}
      >
        {/* 요약 밴드는 스크롤 영역 밖에 있어 항상 상단에 보인다.
            좁은 폭에서는 요약 문구와 체크박스가 한 줄에 들어가지 않아 줄바꿈을 허용한다. */}
        <Flex
          flexShrink={0}
          justifyContent={"space-between"}
          alignItems={"center"}
          flexWrap={"wrap"}
          gap={"8px"}
          padding={"12px 16px"}
          borderBottom={CELL_BORDER}
        >
          <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
            <CustomIcon src={"cart"} width={18} height={18} color={"#444440"} />
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:basket.order_modal.selected_products")}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {t("path_pick:search_products.popular.product_count_value", {
                count: items.length,
              })}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:basket.order_modal.order_quantity")}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {t("path_pick:search_products.popular.product_count_value", {
                count: totalQuantity,
              })}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t("path_pick:basket.order_modal.total_order_amount")}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
              {formatKrw(totalAmount)}
            </Text>
          </Flex>
          <AppCheckBox
            size={"small"}
            isChecked={showOnlyWithQuantity}
            onChange={setShowOnlyWithQuantity}
            name={t("path_pick:basket.order_modal.only_with_quantity")}
          />
        </Flex>

        {/* 가로·세로 스크롤을 모두 맡는 유일한 스크롤 요소.
            minHeight={0}이 없으면 flex 아이템이 내용 높이만큼 부풀어 스크롤이 생기지 않는다. */}
        <Box ref={scrollRef} flex={1} minHeight={0} overflow={"auto"}>
          <Box minWidth={"fit-content"}>
            {/* 헤더 row — 세로 스크롤 시 상단 고정 */}
            <Grid
              templateColumns={templateColumns}
              position={"sticky"}
              top={0}
              zIndex={2}
            >
              <Flex
                {...headerCellStyle}
                position={"sticky"}
                left={0}
                zIndex={3}
              >
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:basket.order_modal.column_product")}
                </Text>
              </Flex>
              <Flex {...headerCellStyle}>
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:filter.category")}
                </Text>
              </Flex>
              <Flex {...headerCellStyle}>
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:basket.order_modal.column_season_color")}
                </Text>
              </Flex>
              <Flex {...headerCellStyle}>
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:basket.order_modal.column_unit_price")}
                </Text>
              </Flex>
              <Flex {...headerCellStyle}>
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:basket.order_modal.column_order_amount")}
                </Text>
              </Flex>
              {/* 옵션 칼럼 헤더는 고정 라벨, 실제 옵션명은 각 행에 표시한다. */}
              {Array.from({ length: maxOptionCount }, (_, columnIndex) => (
                <Flex
                  key={columnIndex}
                  {...headerCellStyle}
                  flexDirection={"column"}
                  gap={"2px"}
                >
                  <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                    {t("path_pick:basket.order_modal.column_option_name")}
                  </Text>
                  <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                    {t("path_pick:filter.stock")}
                  </Text>
                  <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                    {t("path_pick:basket.order_modal.column_quantity")}
                  </Text>
                </Flex>
              ))}
              <Flex {...headerFillerCellStyle} />
            </Grid>

            {paddingTop > 0 && <Box height={`${paddingTop}px`} />}

            {/* 상품 rows — 보이는 구간만 DOM에 만든다. */}
            {virtualRows.map((virtualRow) => {
              const item = visibleItems[virtualRow.index];
              const itemOptions = item.options;

              return (
                <Grid
                  key={toItemKey(item)}
                  templateColumns={templateColumns}
                  height={`${rowHeight}px`}
                >
                  <Flex
                    {...bodyCellStyle}
                    position={"sticky"}
                    left={0}
                    zIndex={1}
                    gap={"8px"}
                    alignItems="center"
                  >
                    <Box position={"relative"} flexShrink={0}>
                      <Image
                        src={getProductImageUrl(item)}
                        fallbackSrc={"/icons-v1_1/null.png"}
                        boxSize={"56px"}
                        objectFit={"contain"}
                        border={CELL_BORDER}
                        borderRadius={"8px"}
                        alt={item.name}
                      />
                      {isProductImageAiCollected(item) && (
                        <PathPickAiImageBadge size={"small"} />
                      )}
                    </Box>
                    <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
                      <Text
                        textStyle={"body-sm-bold"}
                        color={"#1D1D1B"}
                        noOfLines={1}
                      >
                        {item.brandName}
                      </Text>
                      <Text
                        textStyle={"body-sm-medium"}
                        color={"#444440"}
                        noOfLines={1}
                      >
                        {item.name}
                      </Text>
                      <Text
                        textStyle={"caption-medium"}
                        color={"#8F8F8C"}
                        noOfLines={1}
                      >
                        {`${item.sku}${item.gender ? ` · ${item.gender}` : ""}`}
                      </Text>
                      <Flex gap={"4px"} alignItems={"center"}>
                        <StatusBadge
                          status={t(
                            item.productType === "LINE_SHEET_PRODUCT"
                              ? "path_pick:search_products.size_stock_modal.product_type_line_sheet_product"
                              : "path_pick:search_products.size_stock_modal.product_type_product"
                          )}
                          colorVariant={"blue"}
                          size={"small"}
                        />
                        <AppButton
                          variant={"white"}
                          size={"small"}
                          isDisabled={isBuyAllDisabled(item)}
                          onClick={() => handleBuyAll(item)}
                        >
                          <Text textStyle={"caption-bold"}>
                            {t("path_pick:basket.order_modal.buy_all")}
                          </Text>
                        </AppButton>
                      </Flex>
                    </Flex>
                  </Flex>
                  <Flex {...bodyCellStyle} alignItems={"center"}>
                    <Text
                      textStyle={"body-sm-bold"}
                      color={"#444440"}
                      noOfLines={3}
                    >
                      {/*{item.categories.join(" / ")}*/}
                      {item.pathradeCategory}
                    </Text>
                  </Flex>
                  <Flex
                    {...bodyCellStyle}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    gap={"2px"}
                  >
                    <Text textStyle={"body-sm-bold"} color={"#444440"}>
                      {item.season}
                    </Text>
                    <Text
                      textStyle={"body-sm-medium"}
                      color={"#8F8F8C"}
                      noOfLines={1}
                    >
                      {item.color}
                    </Text>
                  </Flex>
                  <Flex {...bodyCellStyle} alignItems={"center"}>
                    <Text textStyle={"body-sm-bold"} color={"#444440"}>
                      {formatKrw(item.salePrice.value)}
                    </Text>
                  </Flex>
                  <Flex {...bodyCellStyle} alignItems={"center"}>
                    <Text textStyle={"body-sm-bold"} color={"#444440"}>
                      {formatKrw(getItemAmount(item))}
                    </Text>
                  </Flex>
                  {/* 옵션 셀은 보유한 옵션을 1번 칼럼부터 채우고 뒤쪽은 빈 셀로 남긴다.
                      표시명이 중복될 수 있어 key는 인덱스를 쓴다. */}
                  {Array.from({ length: maxOptionCount }, (_, columnIndex) => {
                    const option = itemOptions[columnIndex];

                    if (!option) {
                      return <Flex key={columnIndex} {...bodyCellStyle} />;
                    }

                    const quantity = getItemQuantity(item, option.name);
                    const required = getRequiredQuantity(item);
                    const isItemQuantityMet =
                      getItemTotalQuantity(item) === required;

                    return (
                      <Flex
                        key={columnIndex}
                        {...bodyCellStyle}
                        flexDirection={"column"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        gap={"4px"}
                      >
                        <Text
                          textStyle={"body-sm-bold"}
                          color={"#444440"}
                          noOfLines={1}
                        >
                          {option.displayName || option.name}
                        </Text>
                        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                          {t("path_pick:basket.order_modal.stock_value", {
                            count: option.stock,
                          })}
                        </Text>
                        <Input
                          type={"number"}
                          min={0}
                          max={option.stock}
                          // 0은 실제 값이 아니라 placeholder로 보여준다.
                          // value에 "0"이 들어 있으면 캐럿이 그 앞에 놓였을 때 "10"처럼 덧붙는다.
                          // 문자열로 넘기는 것도 중요하다 — 숫자를 넘기면 react-dom이
                          // type=number 예외 분기에서 "01" != 1 을 느슨한 비교로 같다고 보고
                          // DOM 갱신을 건너뛴다.
                          value={quantity === 0 ? "" : String(quantity)}
                          placeholder={"0"}
                          _placeholder={{ color: "#8F8F8C" }}
                          onChange={(event) =>
                            handleChangeQuantity(
                              item,
                              option.name,
                              event.target.value,
                              option.stock
                            )
                          }
                          // type=number는 포커스 상태에서 휠 스크롤이 값을 증감시킨다.
                          // 세로 스크롤되는 표라 수량이 모르게 바뀌므로 blur로 차단한다.
                          onWheel={(event) => event.currentTarget.blur()}
                          width={"64px"}
                          height={"32px"}
                          fontSize={"12px"}
                          textAlign={"center"}
                          borderColor={"#D4D4D1"}
                          _focusVisible={{
                            borderColor: "#1272EF",
                            boxShadow: "none",
                          }}
                        />
                        {/* 가이드에서만 붙는 필수 수량 안내 — 눌리지 않는 주문 버튼의 이유를
                            입력 바로 옆에서 보게 한다. 충족하면 색으로 그것도 알린다. */}
                        {required !== undefined && (
                          <Text
                            textStyle={"caption-medium"}
                            color={isItemQuantityMet ? "#247A52" : "#EE7D7A"}
                            textAlign={"center"}
                          >
                            {t(
                              "path_pick:basket.order_modal.required_quantity_hint",
                              { count: required }
                            )}
                          </Text>
                        )}
                      </Flex>
                    );
                  })}
                  <Flex {...bodyFillerCellStyle} />
                </Grid>
              );
            })}

            {paddingBottom > 0 && <Box height={`${paddingBottom}px`} />}
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter padding={"12px 16px"} borderTop={CELL_BORDER}>
        {/* 좁은 폭에서는 합계 박스와 버튼을 위아래로 둔다. */}
        <Flex
          width={"100%"}
          flexDirection={{ base: "column", md: "row" }}
          justifyContent={"space-between"}
          alignItems={{ base: "stretch", md: "flex-end" }}
          gap={{ base: "12px", md: "16px" }}
        >
          <Flex
            gap={"24px"}
            padding={"12px 16px"}
            borderRadius={"8px"}
            background={"#1D1D1B"}
          >
            <Flex flexDirection={"column"} gap={"2px"}>
              <Text textStyle={"caption-medium"} color={"#C0C0C0"}>
                {t("path_pick:basket.order_modal.total_quantity")}
              </Text>
              <Text textStyle={"body-lg-bold"} color={"#FFFFFF"}>
                {tNumberString(totalQuantity)}
              </Text>
            </Flex>
            <Flex flexDirection={"column"} gap={"2px"}>
              <Text textStyle={"caption-medium"} color={"#C0C0C0"}>
                {t("path_pick:basket.order_modal.total_order_amount")}
              </Text>
              <Text textStyle={"body-lg-bold"} color={"#FFFFFF"}>
                {formatKrw(totalAmount)}
              </Text>
            </Flex>
          </Flex>
          <Flex gap={"8px"} justifyContent={"flex-end"}>
            <AppButton variant={"white"} onClick={onClose}>
              <Text textStyle={"body-sm-bold"}>{t("common:close")}</Text>
            </AppButton>
            <AppButton
              variant={"primary"}
              type={"submit"}
              isDisabled={totalQuantity === 0 || !isRequiredQuantityMet}
              isLoading={isSubmitting}
            >
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:basket.order_modal.request_order")}
              </Text>
            </AppButton>
          </Flex>
        </Flex>
      </ModalFooter>
    </Form>
  );
}

function PathPickOrderQuantityModal({
  isOpen,
  onClose,
  items,
  onOrderCompleted,
  isTutorial = false,
  requiredQuantityByProductId,
}: Props) {
  const { t } = useI18nHelper();
  const { mutate: createOrder, isPending } = useCreatePathPickOrder();
  // 주문이 접수되면 완료 모달로 넘어간다. 응답에 입금 정보와 주문 id가 다 들어 있어 추가 조회가 없다.
  const [createdOrder, setCreatedOrder] =
    useState<PathPickCreatedOrderResponse | null>(null);
  // 수량 입력을 마치고 조립해 둔 payload — 배송지를 확인한 뒤에 그대로 POST한다.
  const [pendingOrderItems, setPendingOrderItems] = useState<
    PathPickOrderItemRequestBody[] | null
  >(null);
  const [isShippingOpen, setIsShippingOpen] = useState(false);
  // 입금자명 — 서버 필수값(1~100자). 선입금 구조라 어드민이 이 값으로 입금 내역을 대조한다.
  // 배송지 확인(주문 직전 마지막 단계)에서 함께 받는다.
  const [remitterName, setRemitterName] = useState("");
  // 프리필용 — 이 바이어가 지난 주문·멤버십 신청에 마지막으로 적은 입금자명(서버가 기억해 둔다).
  // 한 번도 적은 적이 없으면 빈 칸으로 둔다 — 회사명을 대신 채우면 실제 입금자명과 다른 채로 넘어간다.
  const { remitterName: savedRemitterName } = useGetBuyerRemitterName();
  const queryClient = useQueryClient();
  // 환불 계좌 — 주문 시점 스냅샷(선택). 비워두면 서버가 기본 환불 계좌를 복사한다.
  // 기본 계좌가 있으면 미리 채워 보여준다(바이어가 이 주문만 다른 계좌로 바꿀 수 있게).
  const { buyerBankInfo } = useGetBuyerBankInfo();
  const [refundBankName, setRefundBankName] = useState("");
  const [refundAccountNumber, setRefundAccountNumber] = useState("");
  const [refundAccountHolder, setRefundAccountHolder] = useState("");
  const [saveRefundAsDefault, setSaveRefundAsDefault] = useState(false);
  // 셋 중 일부만 채우면 서버가 400 — 제출 버튼에서 막는다(전부 비우는 것은 허용).
  const refundFilledCount = [
    refundBankName.trim(),
    refundAccountNumber.trim(),
    refundAccountHolder.trim(),
  ].filter(Boolean).length;
  const isRefundPartial = refundFilledCount > 0 && refundFilledCount < 3;

  const initialValues = useMemo<OrderQuantityFormValues>(
    () => ({ quantities: buildInitialQuantities(items) }),
    [items]
  );

  const handleSubmit = (values: OrderQuantityFormValues) => {
    const orderItems: PathPickOrderItemRequestBody[] = items
      .map((item) => ({
        // 상품은 타입 + 해당 테이블 id 조합으로 특정한다 (둘 다 필수).
        productType: item.productType,
        productId: item.id,
        // 서버는 화면 표시명이 아니라 option.name을 기준으로 옵션을 찾는다.
        options: item.options
          .map((option) => ({
            name: option.name,
            quantity: values.quantities[toItemKey(item)]?.[option.name] ?? 0,
          }))
          .filter((option) => option.quantity > 0),
      }))
      // 수량이 하나도 없는 상품은 payload에서 제외한다.
      .filter((orderItem) => orderItem.options.length > 0);

    // 제출 버튼이 이미 막고 있지만, 빈 요청은 400이라 한 번 더 방어한다.
    if (orderItems.length === 0) {
      return;
    }

    // 가이드에서는 여기서 끝낸다 — 이후 단계는 전부 실제 주문을 만드는 경로다.
    if (isTutorial) {
      onOrderCompleted?.(orderItems.map(toOrderedProduct));
      onClose();
      return;
    }

    // 주문 생성은 배송지 확인 단계를 통과한 뒤에 한다.
    setPendingOrderItems(orderItems);
    // 비어 있을 때만 저장된 입금자명으로 미리 채운다 — 바이어가 고쳐 둔 값은 덮지 않는다.
    if (!remitterName.trim() && savedRemitterName) {
      setRemitterName(savedRemitterName);
    }
    // 환불 계좌도 같은 규칙 — 전부 비어 있을 때만 기본 환불 계좌로 채운다.
    if (refundFilledCount === 0 && buyerBankInfo) {
      setRefundBankName(buyerBankInfo.bankName ?? "");
      setRefundAccountNumber(buyerBankInfo.accountNumber ?? "");
      setRefundAccountHolder(buyerBankInfo.accountHolder ?? "");
    }
    // 기본 계좌가 없는 바이어는 이번 입력을 기본으로 저장하도록 미리 체크해 둔다.
    if (!buyerBankInfo) {
      setSaveRefundAsDefault(true);
    }
    setIsShippingOpen(true);
  };

  const handleConfirmShipping = (shippingInfo: ShippingInfoResponse) => {
    if (!pendingOrderItems || !remitterName.trim() || isRefundPartial) {
      return;
    }

    createOrder(
      {
        items: pendingOrderItems,
        shippingInfoId: shippingInfo.id,
        remitterName: remitterName.trim(),
        // 셋 다 채웠을 때만 보낸다 — 전부 비우면 서버가 기본 환불 계좌를 스냅샷한다.
        ...(refundFilledCount === 3
          ? {
              refundBankName: refundBankName.trim(),
              refundAccountNumber: refundAccountNumber.trim(),
              refundAccountHolder: refundAccountHolder.trim(),
              saveRefundAccountAsDefault: saveRefundAsDefault,
            }
          : {}),
      },
      {
        onSuccess: (order) => {
          setIsShippingOpen(false);
          setCreatedOrder(order);
          // 서버가 이번 입금자명을 기본값으로 기억했다 — 다음 주문 때 새 값이 채워지게 한다.
          queryClient.invalidateQueries({
            queryKey: [buyerRemitterNameQueryKey],
          });
          onOrderCompleted?.(pendingOrderItems.map(toOrderedProduct));
        },
      }
    );
  };

  return (
    <>
      {/* 접수되면 수량 모달은 시각적으로만 닫는다 — 부모가 마운트를 쥐고 있어
          onClose를 지금 부르면 완료 모달까지 같이 사라진다. */}
      <CustomApprovalModal
        isOpen={isOpen && !createdOrder}
        onClose={onClose}
        size={"full"}
        // 공용 모달의 기본 minWidth(480px)는 max-width(size="full"의 100vw)보다 우선해,
        // 480px보다 좁은 화면에서 모달이 뷰포트보다 넓어지고 가운데 정렬로 양옆이 잘린다.
        // PC는 width 100%(=뷰포트)가 이미 480px보다 넓어 이 값을 풀어도 영향이 없다.
        minWidth={"0"}
        scrollBehavior={"inside"}
      >
        <Formik<OrderQuantityFormValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          <OrderQuantityForm
            items={items}
            onClose={onClose}
            isSubmitting={isPending}
            requiredQuantityByProductId={requiredQuantityByProductId}
          />
        </Formik>
      </CustomApprovalModal>

      {/* 수량 모달은 열어둔 채로 위에 쌓는다 — Chakra는 닫힌 모달의 자식을 언마운트하므로
          숨기면 Formik 인스턴스와 함께 입력한 수량·필터·스크롤 위치가 모두 사라진다.
          겹침 순서는 포탈 노드의 DOM 순서로 결정돼 나중에 마운트되는 이 모달이 위에 그려진다.
          onClose는 부모의 onClose가 아니다 — 취소는 수량 입력으로 돌아가는 동작이다. */}
      {isShippingOpen && !createdOrder && (
        <ShippingInfoSelectModal
          isOpen={true}
          onClose={() => setIsShippingOpen(false)}
          onSubmit={handleConfirmShipping}
          title={t("path_pick:basket.order_modal.shipping_confirm_title")}
          confirmName={t(
            "path_pick:basket.order_modal.shipping_confirm_submit"
          )}
          isConfirmLoading={isPending}
          isConfirmDisabled={
            remitterName.trim().length === 0 || isRefundPartial
          }
          extraContent={
            <Flex flexDirection={"column"} gap={"8px"} marginTop={"20px"}>
              {/* 크기는 시안(프로토타입) 실측에 맞춘다 — 라벨 13px/bold = body-sm-bold. */}
              <Flex gap={"2px"} alignItems={"center"}>
                <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                  {t("path_pick:basket.order_modal.remitter_name_label")}
                </Text>
                {/* 필수 표시 — BuyerInfoModal 의 FieldLabel 과 같은 색 */}
                <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
                  {"*"}
                </Text>
              </Flex>
              <Input
                value={remitterName}
                maxLength={100}
                size={"sm"}
                fontSize={"12px"}
                height={"32px"}
                borderRadius={"4px"}
                placeholder={t(
                  "path_pick:basket.order_modal.remitter_name_placeholder"
                )}
                _placeholder={{ color: "#8F8F8C" }}
                onChange={(event) => setRemitterName(event.target.value)}
              />
              <Text textStyle={"caption-regular"} color={"#6F6F6C"}>
                {t("path_pick:basket.order_modal.remitter_name_help")}
              </Text>

              {/* 환불 계좌 — 선택 입력. 비워두면 기본 환불 계좌가 스냅샷된다. */}
              <Flex gap={"2px"} alignItems={"center"} marginTop={"12px"}>
                <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                  {t("path_pick:basket.order_modal.refund_account_label")}
                </Text>
              </Flex>
              {/* 모달이 화면 폭으로 줄어드는 모바일에서는 세 칸을 세로로 쌓는다. */}
              <Flex gap={"8px"} flexDirection={{ base: "column", md: "row" }}>
                <Input
                  value={refundBankName}
                  maxLength={50}
                  size={"sm"}
                  fontSize={"12px"}
                  height={"32px"}
                  borderRadius={"4px"}
                  placeholder={t(
                    "path_pick:basket.order_modal.refund_account_bank_placeholder"
                  )}
                  _placeholder={{ color: "#8F8F8C" }}
                  onChange={(event) => setRefundBankName(event.target.value)}
                />
                <Input
                  value={refundAccountNumber}
                  maxLength={50}
                  size={"sm"}
                  fontSize={"12px"}
                  height={"32px"}
                  borderRadius={"4px"}
                  placeholder={t(
                    "path_pick:basket.order_modal.refund_account_number_placeholder"
                  )}
                  _placeholder={{ color: "#8F8F8C" }}
                  onChange={(event) =>
                    setRefundAccountNumber(event.target.value)
                  }
                />
                <Input
                  value={refundAccountHolder}
                  maxLength={50}
                  size={"sm"}
                  fontSize={"12px"}
                  height={"32px"}
                  borderRadius={"4px"}
                  placeholder={t(
                    "path_pick:basket.order_modal.refund_account_holder_placeholder"
                  )}
                  _placeholder={{ color: "#8F8F8C" }}
                  onChange={(event) =>
                    setRefundAccountHolder(event.target.value)
                  }
                />
              </Flex>
              <Text textStyle={"caption-regular"} color={"#6F6F6C"}>
                {t("path_pick:basket.order_modal.refund_account_help")}
              </Text>
              {isRefundPartial && (
                <Text textStyle={"caption-regular"} color={"#EE7D7A"}>
                  {t("path_pick:basket.order_modal.refund_account_partial")}
                </Text>
              )}
              <Flex gap={"6px"} alignItems={"center"}>
                <AppCheckBox
                  isChecked={saveRefundAsDefault}
                  size={"small"}
                  onChange={(checked) => setSaveRefundAsDefault(checked)}
                />
                <Text
                  textStyle={"caption-regular"}
                  color={"#444440"}
                  cursor={"pointer"}
                  onClick={() => setSaveRefundAsDefault((prev) => !prev)}
                >
                  {t(
                    "path_pick:basket.order_modal.refund_account_save_default"
                  )}
                </Text>
              </Flex>
            </Flex>
          }
        />
      )}

      {createdOrder && (
        <PathPickOrderCompleteModal
          isOpen={true}
          onClose={onClose}
          order={createdOrder}
        />
      )}
    </>
  );
}

export default PathPickOrderQuantityModal;
